import { DB_SAMPLES, DBCollection, DBModel, DBSentence, SentenceStatus, Table } from '../models/db_models';

const DB_NAME = '10000sentences';
const DB_VERSION = 1;

let db: IDBDatabase | null = null;

function addTableColumn(table: Table, res: any) {
  if (!res) {
    return res;
  }
  if (Array.isArray(res)) {
    (res as any[]).forEach(r => {
      (r as DBModel).TABLE = table;
    });
  } else {
      (res as DBModel).TABLE = table;
  }
  return res;
}

export const initDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;

      for (const model of DB_SAMPLES) {
        if (!database.objectStoreNames.contains(model.TABLE)) {
          const store = database.createObjectStore(model.TABLE, { keyPath: 'id' });

          // Create indexes for sentences
          if (model.TABLE === Table.SENTENCES) {
            store.createIndex('collectionId', 'collectionId', { unique: false });
          }
        }
      }
    };
  });
};

export const save = async (e: DBModel): Promise<void> => {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([e.TABLE], 'readwrite');
    const store = transaction.objectStore(e.TABLE);
    const request = store.put(e);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const saveAll = async (all: DBModel[]): Promise<void> => {
  if (!all?.length) return;

  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([all[0].TABLE], 'readwrite');
    const store = transaction.objectStore(all[0].TABLE);

    let completed = 0;
    const total = all.length;

    all.forEach(e => {
      const request = store.put(e);
      request.onsuccess = () => {
        completed++;
        if (completed === total) {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  });
};

export const deleteEntity = async (m: DBModel): Promise<void> => {
  return deleteById(m.TABLE, m.id);
}

export const deleteById = async (table: Table, id: string): Promise<void> => {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([table], 'readwrite');
    const store = transaction.objectStore(table);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export const getById = async (table: Table, id: string): Promise<DBModel | undefined> => {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([table], 'readonly');
    const store = transaction.objectStore(table);
    const request = store.get(id);

    request.onsuccess = () => resolve(addTableColumn(table, request.result));
    request.onerror = () => reject(request.error);
  });
};

export const getByIndex = async (table: Table, indexColumn: string, value: string): Promise<DBModel[]> => {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([table], 'readonly');
    const store = transaction.objectStore(table);
    const index = store.index(indexColumn);
    const request = index.getAll(value);

    request.onsuccess = () => resolve(addTableColumn(table, request.result));
    request.onerror = () => reject(request.error);
  });
};

export const loadAll = async (table: Table): Promise<DBModel[]> => {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([table], 'readonly');
    const store = transaction.objectStore(table);
    const request = store.getAll();

    request.onsuccess = () => resolve(addTableColumn(table, request.result));
    request.onerror = () => reject(request.error);
  });
};

export const getFirstSentenceByStatus = async (
  collectionId: string,
  status: SentenceStatus
): Promise<DBSentence | undefined> => {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([Table.SENTENCES], 'readonly');
    const store = transaction.objectStore(Table.SENTENCES);
    const index = store.index('collectionId');
    const request = index.getAll(collectionId);

    request.onsuccess = () => {
      const sentences = request.result as DBSentence[];
      const filtered = sentences
        .filter(s => s.status === status)
        .sort((a, b) => a.order - b.order);
      resolve(addTableColumn(Table.SENTENCES, filtered[0]));
    };
    request.onerror = () => reject(request.error);
  });
};

export const getRandomSentences = async (
  collectionId: string,
  count: number
): Promise<DBSentence[]> => {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([Table.SENTENCES], 'readonly');
    const store = transaction.objectStore(Table.SENTENCES);
    const index = store.index('collectionId');
    const request = index.getAll(collectionId);

    request.onsuccess = () => {
      const sentences = request.result as DBSentence[];
      const shuffled = sentences.sort(() => Math.random() - 0.5);
      resolve(addTableColumn(Table.SENTENCES, shuffled.slice(0, count)));
    };
    request.onerror = () => reject(request.error);
  });
};

export const updateCollectionCounts = async (collectionId: string): Promise<void> => {
  const sentences = await getByIndex(Table.SENTENCES, 'collectionId', collectionId) as DBSentence[];

  const collection = await getById(Table.COLLECTIONS, collectionId) as DBCollection | undefined;
  if (collection) {
    collection.doneCount = sentences.filter(s => s.status === SentenceStatus.DONE).length;
    collection.todoCount = sentences.filter(s => s.status === SentenceStatus.TODO).length;
    collection.repeatCount = sentences.filter(s => s.status === SentenceStatus.REPEAT).length;
    await save(collection);
  }
};
